import {UnitsSystem} from '@/Pages/Common/LocalUnits/types';
import {ValueDimension} from '@/Pages/Countries/components/index.constants';
import {Country} from '@/Pages/Countries/store/types';
import {PartnerCostsVehicleCost} from '@/Pages/PartnerCosts/store/types';
import {TableWithPanelHeader} from '@/components/TableWithPanel';
import {VehicleCostFieldConfig} from '@/components/VehicleCosts/VehicleCostsTable/VehicleCostsTable.types';

export const getPartnerCostsVehicleCostTableConfig = (
  vehicleCost: PartnerCostsVehicleCost | null,
  country: Country,
  localUnits: UnitsSystem
): VehicleCostFieldConfig[] => {
  const getDamageRepairHeader: () => VehicleCostFieldConfig = () => {
    const repairCostDimension = vehicleCost?.repairCostPerDistanceUnit
      ? ValueDimension.PER_DISTANCE_UNIT
      : vehicleCost?.repairCostPerMonth
      ? ValueDimension.PER_MONTH
      : country.repairCostDimension;

    return repairCostDimension === ValueDimension.PER_DISTANCE_UNIT
      ? {
          name: `Damage repair`,
          unit: `${country.currency}/${localUnits.distanceAbbreviation}`,
          key: 'repairCostPerDistanceUnit',
          integerDigitsCount: 7
        }
      : {
          name: `Damage repair`,
          unit: `${country.currency}/month`,
          key: 'repairCostPerMonth',
          integerDigitsCount: 7
        };
  };

  return [
    {
      name: `Diesel`,
      unit: `${country.currency}/${localUnits.volumeAbbreviation}`,
      key: 'dieselCost',
      mandatory: true
    },
    {
      name: `Ad Blue`,
      unit: `${country.currency}/${localUnits.volumeAbbreviation}`,
      key: 'adblueCost',
      mandatory: true
    },
    {
      name: `LNG`,
      unit: `${country.currency}/${localUnits.weightAbbreviation}`,
      key: 'lngCost'
    },
    {
      name: `Cost per Tire`,
      unit: `${country.currency}/${localUnits.distanceAbbreviation}`,
      key: 'tiresCost',
      fractionDigitsCount: 5,
      mandatory: true
    },
    {
      name: `Overhead per vehicle`,
      unit: `${country.currency}/month`,
      key: 'overheadCost',
      mandatory: true
    },
    {
      name: `Legal per vehicle`,
      unit: `${country.currency}/month`,
      key: 'legalCost'
    },
    getDamageRepairHeader(),
    {
      name: `Cleaning per vehicle`,
      unit: `${country.currency}/op. day`,
      key: 'cleaningCost',
      mandatory: true
    },
    {
      name: `Other costs per vehicle`,
      unit: `${country.currency}/month`,
      key: 'otherCost'
    }
  ];
};

export const getCostPerVehicleTypeHeaders: (
  currency: string,
  localUnits: UnitsSystem
) => TableWithPanelHeader[] = (currency, localUnits) => {
  return [
    {
      key: 'busTypeName',
      name: 'Vehicle type'
    },
    {
      key: 'fuelTypeName',
      name: 'Fuel type'
    },
    {
      key: 'customIdentifier',
      name: 'Custom identifier',
      displayName: (
        <>
          <div>Custom</div>
          <div>identifier</div>
        </>
      )
    },
    {
      key: 'vehicleProductionYear',
      name: 'Vehicle production year',
      displayName: (
        <>
          <div>Vehicle</div>
          <div>production</div>
          <div>year</div>
        </>
      )
    },
    {
      key: 'leasingCost',
      name: `Leasing/amortisation cost (${currency}/month)*`,
      displayName: (
        <>
          <div>{`Leasing/amortisation`}</div>
          <div>{`cost`}</div>
          <div>{`(${currency}/month)`}*</div>
        </>
      )
    },
    {
      key: 'insuranceCost',
      name: `Insurance cost (${currency}/month)*`,
      displayName: (
        <>
          <div>Insurance</div>
          <div>cost</div>
          <div>({currency}/month)*</div>
        </>
      )
    },
    {
      key: 'interestCost',
      name: `Interest cost (${currency}/month)`,
      displayName: (
        <>
          <div>Interest</div>
          <div>cost</div>
          <div>({currency}/month)</div>
        </>
      )
    },
    {
      key: 'motorVehicleTax',
      name: `Motor vehicle tax (${currency}/month)`,
      displayName: (
        <>
          <div>Motor</div>
          <div>vehicle tax</div>
          <div>({currency}/month)</div>
        </>
      )
    },
    {
      key: 'maintenanceCostPerMonth',
      name: `Service & Maintenance (${currency}/month)*`,
      displayName: (
        <>
          <div>Service &</div>
          <div>Maintenance</div>
          <div>({currency}/month)*</div>
        </>
      )
    },
    {
      key: 'maintenanceCostPerDistanceUnit',
      name: `Service & Maintenance (${currency}/${localUnits.distanceAbbreviation})*`,
      displayName: (
        <>
          <div>Service &</div>
          <div>Maintenance</div>
          <div>
            ({currency}/{localUnits.distanceAbbreviation})*
          </div>
        </>
      )
    },
    {
      key: 'vehicleRegistrationCost',
      name: `Vehicle Registration Cost (${currency}/month)*`,
      displayName: (
        <>
          <div>Vehicle</div>
          <div>registration cost</div>
          <div>({currency}/month)*</div>
        </>
      )
    }
  ];
};
